import React, { useState, useEffect } from 'react';
import { projectFirestore } from '../../firebase/config';
import { DEFAULT_LLM_PROVIDERS, DEFAULT_SYSTEM_PROMPT, testLLMProvider, fetchModels } from '../../services/llmService';

const DEFAULT_IMAGE_PROVIDERS = [
    { id: 'flux-schnell', name: 'FLUX.1 Schnell', spaceId: 'black-forest-labs/FLUX.1-schnell', endpoint: '/infer', enabled: true, order: 0 },
    { id: 'flux-dev', name: 'FLUX.1 Dev', spaceId: 'black-forest-labs/FLUX.1-dev', endpoint: '/infer', enabled: true, order: 1 },
    { id: 'shuttle-aesthetic', name: 'Shuttle 3.1 Aesthetic', spaceId: 'shuttleai/shuttle-3.1-aesthetic', endpoint: '/infer', enabled: true, order: 2 },
];

const LLM_TYPES = [
    { value: 'openrouter', label: 'OpenRouter', needsKey: true },
    { value: 'groq', label: 'Groq', needsKey: true },
    { value: 'anthropic', label: 'Anthropic (Claude)', needsKey: true },
    { value: 'gemini', label: 'Google Gemini', needsKey: true },
    { value: 'cohere', label: 'Cohere', needsKey: true },
    { value: 'mistral', label: 'Mistral', needsKey: true },
    { value: 'huggingface', label: 'HuggingFace', needsKey: true },
    { value: 'custom', label: 'مخصص (OpenAI-compatible)', needsKey: true },
    { value: 'custom-anthropic', label: 'مخصص (Anthropic-compatible)', needsKey: true },
];

// حذف القيم undefined من الكائنات قبل إرسالها لـ Firestore
const removeUndefined = (obj) => JSON.parse(JSON.stringify(obj));

const AIManager = () => {
    // === إعدادات توليد الصور ===
    const [aiSettings, setAiSettings] = useState({ enabled: true, requireLogin: false, maxPerDay: 10, providers: [] });
    const [newProviderName, setNewProviderName] = useState('');
    const [newProviderSpace, setNewProviderSpace] = useState('');
    const [newProviderEndpoint, setNewProviderEndpoint] = useState('/infer');

    // === إعدادات LLM (الشات بوت) ===
    const [llmSettings, setLlmSettings] = useState({ enabled: true, systemPrompt: DEFAULT_SYSTEM_PROMPT, providers: [] });
    const [apiKeys, setApiKeys] = useState({});

    // === إضافة مزود LLM جديد ===
    const [newLlmType, setNewLlmType] = useState('openrouter');
    const [newLlmName, setNewLlmName] = useState('');
    const [newLlmModel, setNewLlmModel] = useState('');
    const [newLlmBaseUrl, setNewLlmBaseUrl] = useState('');

    // === حالة اختبار الاتصال ===
    const [testingId, setTestingId] = useState('');
    const [testResult, setTestResult] = useState(null);

    // === قائمة النماذج ===
    const [modelsList, setModelsList] = useState([]);
    const [modelsLoading, setModelsLoading] = useState(false);
    const [modelsFilter, setModelsFilter] = useState('');

    // === الصور المولدة ===
    const [aiImages, setAiImages] = useState([]);
    const [imagesLoading, setImagesLoading] = useState(true);

    // === القسم النشط ===
    const [activeSection, setActiveSection] = useState('chatbot');

    // === تحميل البيانات ===
    useEffect(() => {
        const unsub1 = projectFirestore.collection('settings').doc('ai')
            .onSnapshot((doc) => {
                if (doc.exists) {
                    const d = doc.data();
                    setAiSettings({
                        enabled: d.enabled !== false,
                        requireLogin: d.requireLogin || false,
                        maxPerDay: d.maxPerDay || 10,
                        providers: d.providers || [],
                    });
                }
            });

        const unsub2 = projectFirestore.collection('settings').doc('ai-llm')
            .onSnapshot((doc) => {
                if (doc.exists) {
                    const d = doc.data();
                    setLlmSettings({
                        enabled: d.enabled !== false,
                        systemPrompt: d.systemPrompt || DEFAULT_SYSTEM_PROMPT,
                        providers: d.providers || [],
                    });
                } else {
                    const defaults = { enabled: true, systemPrompt: DEFAULT_SYSTEM_PROMPT, providers: DEFAULT_LLM_PROVIDERS };
                    projectFirestore.collection('settings').doc('ai-llm').set(removeUndefined(defaults));
                    setLlmSettings(defaults);
                }
            });

        const unsub3 = projectFirestore.collection('settings').doc('ai-keys')
            .onSnapshot((doc) => {
                if (doc.exists) setApiKeys(doc.data());
            });

        return () => { unsub1(); unsub2(); unsub3(); };
    }, []);

    // تحميل الصور المولدة
    useEffect(() => {
        const unsub = projectFirestore.collection('ai-images')
            .orderBy('createdAt', 'desc').limit(50)
            .onSnapshot((snap) => {
                setAiImages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setImagesLoading(false);
            });
        return () => unsub();
    }, []);

    // === حفظ مفتاح API ===
    const saveApiKey = async (type, value) => {
        const updated = { ...apiKeys, [type]: value };
        setApiKeys(updated);
        await projectFirestore.collection('settings').doc('ai-keys').set(updated, { merge: true });
    };

    // === اختبار مزود LLM ===
    const handleTestProvider = async (provider) => {
        setTestingId(provider.id);
        setTestResult(null);
        const result = await testLLMProvider(provider, apiKeys);
        setTestResult({ id: provider.id, ...result });
        setTestingId('');
    };

    // === جلب قائمة النماذج عند تغيير نوع المزود ===
    const loadModels = async (type, customBaseUrl) => {
        setModelsLoading(true);
        setModelsList([]);
        setModelsFilter('');
        const models = await fetchModels(type, apiKeys[type] || '', customBaseUrl || '');
        setModelsList(models);
        setModelsLoading(false);
    };

    // === إضافة مزود LLM ===
    const handleAddLlm = async () => {
        if (!newLlmName.trim() || !newLlmModel.trim()) { alert('أدخل اسم النموذج والموديل'); return; }
        const typeInfo = LLM_TYPES.find(t => t.value === newLlmType);
        const newProv = {
            id: Date.now().toString(),
            type: newLlmType,
            name: newLlmName.trim(),
            model: newLlmModel.trim(),
            requiresKey: typeInfo?.needsKey || false,
            enabled: true,
            order: llmSettings.providers.length,
        };
        if (newLlmBaseUrl.trim()) newProv.baseUrl = newLlmBaseUrl.trim();
        const updated = [...llmSettings.providers, newProv];
        await projectFirestore.collection('settings').doc('ai-llm').set(removeUndefined({ ...llmSettings, providers: updated }), { merge: true });
        setNewLlmName(''); setNewLlmModel(''); setNewLlmBaseUrl('');
    };

    // === الأقسام ===
    const sections = [
        { key: 'chatbot', label: '🤖 الشات بوت', desc: 'إعدادات تحسين البرومبت' },
        { key: 'llm', label: '🧠 خدمات LLM', desc: 'مزودي نماذج اللغة' },
        { key: 'keys', label: '🔑 مفاتيح API', desc: 'مفاتيح الخدمات المدفوعة' },
        { key: 'image', label: '🎨 توليد الصور', desc: 'خدمات HF Spaces' },
        { key: 'gallery', label: '🖼 الصور المولدة', desc: 'إدارة الصور المولدة' },
    ];

    return (
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <h4 className="text-center mb-3">🤖 إدارة الذكاء الاصطناعي</h4>

            {/* تبويبات فرعية */}
            <div className="d-flex justify-content-center flex-wrap gap-1 mb-4">
                {sections.map(s => (
                    <button key={s.key}
                        className={'btn btn-sm ' + (activeSection === s.key ? 'btn-dark' : 'btn-outline-dark')}
                        onClick={() => setActiveSection(s.key)}>
                        {s.label}
                    </button>
                ))}
            </div>

            {/* ========== 1. إعدادات الشات بوت ========== */}
            {activeSection === 'chatbot' && (
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                        <h5 className="card-title mb-3">🤖 إعدادات تحسين البرومبت (الشات بوت)</h5>
                        <p className="text-muted" style={{ fontSize: 13 }}>
                            يستخدم نموذج لغة لتحويل الوصف العربي إلى برومبت إنجليزي احترافي + فلترة المحتوى غير اللائق.
                        </p>
                        <div className="form-check form-switch mb-3">
                            <input className="form-check-input" type="checkbox" checked={llmSettings.enabled}
                                onChange={async (e) => {
                                    const val = e.target.checked;
                                    setLlmSettings(prev => ({ ...prev, enabled: val }));
                                    await projectFirestore.collection('settings').doc('ai-llm').set({ enabled: val }, { merge: true });
                                }} />
                            <label className="form-check-label">{llmSettings.enabled ? 'الشات بوت مفعّل' : 'الشات بوت معطّل'}</label>
                        </div>

                        <div className="mb-3">
                            <label className="form-label fw-bold">System Prompt (تعليمات النموذج)</label>
                            <textarea className="form-control" rows={6} value={llmSettings.systemPrompt}
                                onChange={e => setLlmSettings(prev => ({ ...prev, systemPrompt: e.target.value }))}
                                dir="ltr" style={{ fontSize: 12, fontFamily: 'monospace' }} />
                            <div className="d-flex gap-2 mt-2">
                                <button className="btn btn-primary btn-sm" onClick={async () => {
                                    await projectFirestore.collection('settings').doc('ai-llm').set(
                                        { systemPrompt: llmSettings.systemPrompt }, { merge: true }
                                    );
                                    alert('تم حفظ التعليمات ✓');
                                }}>💾 حفظ التعليمات</button>
                                <button className="btn btn-outline-secondary btn-sm" onClick={() => {
                                    if (window.confirm('استعادة التعليمات الافتراضية؟')) {
                                        setLlmSettings(prev => ({ ...prev, systemPrompt: DEFAULT_SYSTEM_PROMPT }));
                                    }
                                }}>🔄 استعادة الافتراضي</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== 2. مزودي LLM ========== */}
            {activeSection === 'llm' && (
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                        <h5 className="card-title mb-3">🧠 خدمات نماذج اللغة (LLM)</h5>
                        <p className="text-muted" style={{ fontSize: 13 }}>
                            يتم تجربة الخدمات بالترتيب. خدمات OpenRouter المجانية لا تحتاج مفتاح API.
                        </p>

                        {llmSettings.providers.length > 0 && (
                            <div className="list-group mb-3">
                                {[...llmSettings.providers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((prov, idx) => (
                                    <div key={prov.id} className={'list-group-item' + (prov.enabled === false ? ' opacity-50' : '')}>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <strong>{prov.name}</strong>
                                                <span className={'badge ms-2 ' + (prov.enabled !== false ? 'bg-success' : 'bg-secondary')}>
                                                    {prov.enabled !== false ? 'مفعّل' : 'معطّل'}
                                                </span>
                                                {prov.requiresKey && <span className="badge bg-warning ms-1">يحتاج مفتاح</span>}
                                                <small className="d-block text-muted" dir="ltr">{prov.type} — {prov.model}</small>
                                            </div>
                                            <div className="btn-group btn-group-sm">
                                                <button className="btn btn-outline-secondary" disabled={idx === 0} onClick={async () => {
                                                    const sorted = [...llmSettings.providers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                                                    if (idx === 0) return;
                                                    const t = sorted[idx].order; sorted[idx].order = sorted[idx - 1].order; sorted[idx - 1].order = t;
                                                    await projectFirestore.collection('settings').doc('ai-llm').set(removeUndefined({ providers: sorted }), { merge: true });
                                                }}>▲</button>
                                                <button className="btn btn-outline-secondary" disabled={idx >= llmSettings.providers.length - 1} onClick={async () => {
                                                    const sorted = [...llmSettings.providers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                                                    if (idx >= sorted.length - 1) return;
                                                    const t = sorted[idx].order; sorted[idx].order = sorted[idx + 1].order; sorted[idx + 1].order = t;
                                                    await projectFirestore.collection('settings').doc('ai-llm').set(removeUndefined({ providers: sorted }), { merge: true });
                                                }}>▼</button>
                                                <button className="btn btn-outline-info" onClick={() => handleTestProvider(prov)}
                                                    disabled={testingId === prov.id}>
                                                    {testingId === prov.id ? '...' : '🔌 اختبار'}
                                                </button>
                                                <button className={'btn ' + (prov.enabled !== false ? 'btn-outline-warning' : 'btn-outline-success')} onClick={async () => {
                                                    const updated = llmSettings.providers.map(p => p.id === prov.id ? { ...p, enabled: p.enabled === false } : p);
                                                    await projectFirestore.collection('settings').doc('ai-llm').set(removeUndefined({ providers: updated }), { merge: true });
                                                }}>{prov.enabled !== false ? 'تعطيل' : 'تفعيل'}</button>
                                                <button className="btn btn-outline-danger" onClick={async () => {
                                                    if (!window.confirm('حذف ' + prov.name + '؟')) return;
                                                    const updated = llmSettings.providers.filter(p => p.id !== prov.id);
                                                    await projectFirestore.collection('settings').doc('ai-llm').set(removeUndefined({ providers: updated }), { merge: true });
                                                }}>حذف</button>
                                            </div>
                                        </div>
                                        {testResult && testResult.id === prov.id && (
                                            <div className={'mt-2 small ' + (testResult.success ? 'text-success' : 'text-danger')} style={{ wordBreak: 'break-all' }}>
                                                {testResult.success ? '✅ اتصال ناجح: ' + testResult.response : '❌ فشل: ' + testResult.error}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {llmSettings.providers.length === 0 && (
                            <div className="alert alert-info">لا توجد خدمات LLM — أضف خدمة أو استخدم الافتراضية.</div>
                        )}

                        {/* إضافة مزود LLM جديد */}
                        <div className="border rounded p-3 mb-3" style={{ background: '#f8f9fa' }}>
                            <h6 className="mb-2">➕ إضافة خدمة LLM جديدة</h6>
                            <div className="row g-2">
                                <div className="col-md-3">
                                    <label className="form-label" style={{ fontSize: 11 }}>المزود</label>
                                    <select className="form-select form-select-sm" value={newLlmType} onChange={e => {
                                        setNewLlmType(e.target.value);
                                        setNewLlmModel('');
                                        setNewLlmBaseUrl('');
                                        loadModels(e.target.value);
                                    }}>
                                        {LLM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label" style={{ fontSize: 11 }}>اسم مخصص</label>
                                    <input className="form-control form-control-sm" placeholder="اسم الخدمة" value={newLlmName} onChange={e => setNewLlmName(e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label" style={{ fontSize: 11 }}>النموذج</label>
                                    {modelsList.length > 0 ? (
                                        <>
                                            <input className="form-control form-control-sm mb-1" placeholder="🔍 بحث..." value={modelsFilter}
                                                onChange={e => setModelsFilter(e.target.value)} dir="ltr" />
                                            <select className="form-select form-select-sm" value={newLlmModel}
                                                onChange={e => {
                                                    setNewLlmModel(e.target.value);
                                                    if (!newLlmName.trim()) {
                                                        const m = modelsList.find(x => x.id === e.target.value);
                                                        if (m) setNewLlmName(m.name);
                                                    }
                                                }} style={{ maxHeight: 200 }}>
                                                <option value="">-- اختر نموذج --</option>
                                                {modelsList
                                                    .filter(m => !modelsFilter || m.id.toLowerCase().includes(modelsFilter.toLowerCase()) || m.name.toLowerCase().includes(modelsFilter.toLowerCase()))
                                                    .slice(0, 100)
                                                    .map(m => (
                                                        <option key={m.id} value={m.id}>
                                                            {m.free ? '🆓 ' : ''}{m.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        </>
                                    ) : (
                                        <input className="form-control form-control-sm" placeholder="Model ID" value={newLlmModel}
                                            onChange={e => setNewLlmModel(e.target.value)} dir="ltr" />
                                    )}
                                    {modelsLoading && <small className="text-muted">جاري تحميل النماذج...</small>}
                                    {!modelsLoading && modelsList.length === 0 && (
                                        <button className="btn btn-link btn-sm p-0 mt-1" onClick={() => loadModels(newLlmType, newLlmBaseUrl)}>
                                            📋 تحميل قائمة النماذج
                                        </button>
                                    )}
                                </div>
                                <div className="col-md-3">
                                    {(newLlmType === 'custom' || newLlmType === 'custom-anthropic') && (
                                        <>
                                            <label className="form-label" style={{ fontSize: 11 }}>Base URL</label>
                                            <input className="form-control form-control-sm" placeholder="https://..." value={newLlmBaseUrl}
                                                onChange={e => setNewLlmBaseUrl(e.target.value)} dir="ltr" />
                                        </>
                                    )}
                                    <button className="btn btn-success btn-sm w-100 mt-1" onClick={handleAddLlm}>إضافة</button>
                                </div>
                            </div>
                        </div>

                        <button className="btn btn-outline-info btn-sm" onClick={async () => {
                            if (llmSettings.providers.length > 0 && !window.confirm('سيتم إضافة المزودين الافتراضيين. متأكد؟')) return;
                            const existing = llmSettings.providers.map(p => p.id);
                            const toAdd = DEFAULT_LLM_PROVIDERS.filter(p => !existing.includes(p.id))
                                .map((p, i) => ({ ...p, order: llmSettings.providers.length + i }));
                            const merged = [...llmSettings.providers, ...toAdd];
                            await projectFirestore.collection('settings').doc('ai-llm').set(removeUndefined({ ...llmSettings, providers: merged }), { merge: true });
                            alert('تم إضافة ' + toAdd.length + ' خدمة ✓');
                        }}>🔄 إضافة المزودين الافتراضيين (OpenRouter)</button>
                    </div>
                </div>
            )}

            {/* ========== 3. مفاتيح API ========== */}
            {activeSection === 'keys' && (
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                        <h5 className="card-title mb-3">🔑 مفاتيح API</h5>
                        <p className="text-muted" style={{ fontSize: 13 }}>
                            جميع الخدمات تحتاج مفتاح API. يمكنك الحصول على مفاتيح مجانية من مواقع المزودين.
                        </p>
                        {LLM_TYPES.map(t => (
                            <div key={t.value} className="input-group mb-2">
                                <span className="input-group-text" style={{ minWidth: 130 }}>{t.label}</span>
                                <input type="password" className="form-control" dir="ltr"
                                    placeholder={'مفتاح ' + t.label + '...'}
                                    value={apiKeys[t.value] || ''}
                                    onChange={e => setApiKeys(prev => ({ ...prev, [t.value]: e.target.value }))} />
                                <button className="btn btn-primary btn-sm" onClick={() => saveApiKey(t.value, apiKeys[t.value] || '')}>
                                    حفظ
                                </button>
                            </div>
                        ))}
                        <small className="text-muted">
                            💡 OpenRouter — سجل مجاناً في openrouter.ai واحصل على مفتاح. النماذج المجانية (:free) متاحة بدون رصيد.<br/>
                            💡 HuggingFace — توكن مجاني من huggingface.co/settings/tokens<br/>
                            💡 Groq — مفتاح مجاني من console.groq.com
                        </small>
                    </div>
                </div>
            )}

            {/* ========== 4. إعدادات توليد الصور ========== */}
            {activeSection === 'image' && (
                <>
                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h5 className="card-title mb-3">🤖 توليد الصور بالذكاء الاصطناعي (مجاني)</h5>
                            <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" checked={aiSettings.enabled}
                                        onChange={async (e) => {
                                            const enabled = e.target.checked;
                                            setAiSettings(prev => ({ ...prev, enabled }));
                                            await projectFirestore.collection('settings').doc('ai').set({ enabled }, { merge: true });
                                        }} />
                                    <label className="form-check-label">{aiSettings.enabled ? 'مفعّل' : 'معطّل'}</label>
                                </div>
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" checked={aiSettings.requireLogin}
                                        onChange={async (e) => {
                                            const requireLogin = e.target.checked;
                                            setAiSettings(prev => ({ ...prev, requireLogin }));
                                            await projectFirestore.collection('settings').doc('ai').set({ requireLogin }, { merge: true });
                                        }} />
                                    <label className="form-check-label">مطلوب تسجيل دخول</label>
                                </div>
                            </div>
                            <div className="input-group mb-3" style={{ maxWidth: 350 }}>
                                <span className="input-group-text">الحد اليومي</span>
                                <input type="number" className="form-control" min="1" max="100"
                                    value={aiSettings.maxPerDay}
                                    onChange={e => setAiSettings(prev => ({ ...prev, maxPerDay: parseInt(e.target.value) || 10 }))} />
                                <button className="btn btn-primary" onClick={async () => {
                                    await projectFirestore.collection('settings').doc('ai').set({ maxPerDay: aiSettings.maxPerDay }, { merge: true });
                                    alert('تم الحفظ ✓');
                                }}>حفظ</button>
                            </div>
                            <small className="text-muted d-block">
                                🆓 الخدمة مجانية بالكامل عبر Hugging Face Spaces — لا تحتاج مفتاح API.
                                الصفحة متاحة على الرابط <code>/ai-generator</code>
                            </small>
                        </div>
                    </div>

                    <div className="card shadow-sm mb-4">
                        <div className="card-body">
                            <h5 className="card-title mb-3">⚡ خدمات التوليد (Hugging Face Spaces)</h5>
                            <p className="text-muted" style={{ fontSize: 13 }}>يتم التبديل تلقائياً بين الخدمات حسب التوفر. الخدمة الأولى في القائمة تُجرّب أولاً.</p>

                            {aiSettings.providers.length > 0 && (
                                <div className="list-group mb-3">
                                    {[...aiSettings.providers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((prov, idx) => (
                                        <div key={prov.id} className={'list-group-item d-flex justify-content-between align-items-center' + (prov.enabled === false ? ' opacity-50' : '')}>
                                            <div>
                                                <strong>{prov.name}</strong>
                                                {prov.enabled !== false ? <span className="badge bg-success ms-2">مفعل</span> : <span className="badge bg-secondary ms-2">معطل</span>}
                                                <small className="d-block text-muted" dir="ltr">{prov.spaceId} — {prov.endpoint || '/infer'}</small>
                                            </div>
                                            <div className="btn-group btn-group-sm">
                                                <button className="btn btn-outline-secondary" disabled={idx === 0} onClick={async () => {
                                                    const sorted = [...aiSettings.providers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                                                    if (idx === 0) return;
                                                    const t = sorted[idx].order; sorted[idx].order = sorted[idx - 1].order; sorted[idx - 1].order = t;
                                                    await projectFirestore.collection('settings').doc('ai').set({ providers: sorted }, { merge: true });
                                                }}>▲</button>
                                                <button className="btn btn-outline-secondary" disabled={idx >= aiSettings.providers.length - 1} onClick={async () => {
                                                    const sorted = [...aiSettings.providers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                                                    if (idx >= sorted.length - 1) return;
                                                    const t = sorted[idx].order; sorted[idx].order = sorted[idx + 1].order; sorted[idx + 1].order = t;
                                                    await projectFirestore.collection('settings').doc('ai').set({ providers: sorted }, { merge: true });
                                                }}>▼</button>
                                                <button className={'btn ' + (prov.enabled !== false ? 'btn-outline-warning' : 'btn-outline-success')} onClick={async () => {
                                                    const updated = aiSettings.providers.map(p => p.id === prov.id ? { ...p, enabled: p.enabled === false } : p);
                                                    await projectFirestore.collection('settings').doc('ai').set({ providers: updated }, { merge: true });
                                                }}>{prov.enabled !== false ? 'تعطيل' : 'تفعيل'}</button>
                                                <button className="btn btn-outline-danger" onClick={async () => {
                                                    if (!window.confirm('حذف خدمة ' + prov.name + '؟')) return;
                                                    const updated = aiSettings.providers.filter(p => p.id !== prov.id);
                                                    await projectFirestore.collection('settings').doc('ai').set({ providers: updated }, { merge: true });
                                                }}>حذف</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {aiSettings.providers.length === 0 && <p className="text-muted">لا توجد خدمات — أضف خدمة أو اضغط "إضافة الافتراضية"</p>}

                            <div className="row g-2 align-items-end">
                                <div className="col-md-3">
                                    <input type="text" className="form-control form-control-sm" placeholder="اسم الخدمة" value={newProviderName} onChange={e => setNewProviderName(e.target.value)} />
                                </div>
                                <div className="col-md-4">
                                    <input type="text" className="form-control form-control-sm" placeholder="HF Space ID (مثال: owner/name)" value={newProviderSpace} onChange={e => setNewProviderSpace(e.target.value)} dir="ltr" />
                                </div>
                                <div className="col-md-2">
                                    <input type="text" className="form-control form-control-sm" placeholder="Endpoint" value={newProviderEndpoint} onChange={e => setNewProviderEndpoint(e.target.value)} dir="ltr" />
                                </div>
                                <div className="col-md-3">
                                    <button className="btn btn-sm btn-success w-100" onClick={async () => {
                                        if (!newProviderName.trim() || !newProviderSpace.trim()) { alert('أدخل الاسم و Space ID'); return; }
                                        const newProv = {
                                            id: Date.now().toString(),
                                            name: newProviderName.trim(),
                                            spaceId: newProviderSpace.trim(),
                                            endpoint: newProviderEndpoint.trim() || '/infer',
                                            enabled: true,
                                            order: aiSettings.providers.length,
                                        };
                                        const updated = [...aiSettings.providers, newProv];
                                        await projectFirestore.collection('settings').doc('ai').set({ providers: updated }, { merge: true });
                                        setNewProviderName(''); setNewProviderSpace(''); setNewProviderEndpoint('/infer');
                                    }}>➕ إضافة</button>
                                </div>
                            </div>

                            {aiSettings.providers.length === 0 && (
                                <button className="btn btn-outline-info btn-sm mt-2" onClick={async () => {
                                    await projectFirestore.collection('settings').doc('ai').set({ providers: DEFAULT_IMAGE_PROVIDERS }, { merge: true });
                                    alert('تم إضافة 3 خدمات افتراضية ✓');
                                }}>🔄 إضافة الخدمات الافتراضية</button>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* ========== 5. الصور المولدة ========== */}
            {activeSection === 'gallery' && (
                <div className="card shadow-sm mb-4">
                    <div className="card-body">
                        <h5 className="card-title mb-3">🖼 الصور المولّدة ({aiImages.length})</h5>
                        {imagesLoading ? (
                            <div className="text-center py-3"><div className="spinner-border spinner-border-sm"></div></div>
                        ) : aiImages.length === 0 ? (
                            <p className="text-muted">لا توجد صور مولّدة بعد</p>
                        ) : (
                            <div className="row g-2">
                                {aiImages.map(img => (
                                    <div className="col-md-4 col-sm-6" key={img.id}>
                                        <div className="card h-100">
                                            <img src={img.url} alt="" style={{ width: '100%', height: 150, objectFit: 'cover' }} loading="lazy" />
                                            <div className="card-body p-2">
                                                <small className="d-block text-muted" style={{ fontSize: 10, maxHeight: 30, overflow: 'hidden' }}>
                                                    {img.prompt}
                                                </small>
                                                {img.provider && <small className="badge bg-info" style={{ fontSize: 9 }}>{img.provider}</small>}
                                                <button className="btn btn-outline-danger btn-sm d-block mt-1 w-100" onClick={async () => {
                                                    if (!window.confirm('حذف هذه الصورة؟')) return;
                                                    await projectFirestore.collection('ai-images').doc(img.id).delete();
                                                }}>🗑 حذف</button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AIManager;
